/**
 * Complaint Examples - XML-structured borderline calibration
 * Phase 2.2: Examples for complaint handling quality scoring
 */

export const COMPLAINT_EXAMPLES = `
<examples category="complaint">

  <!-- De-escalation: Poor (45) vs Proficient (80) -->

  <example category="de_escalation" score="45" tier="needs_improvement">
    <dialogue>
      <customer speaker="customer" timestamp="01:10">This is unacceptable! I've been waiting 3 weeks!</customer>
      <agent speaker="agent" timestamp="01:15">
        I understand you're upset, but we have a lot of orders to process.
      </agent>
    </dialogue>
    <analysis>
      <component name="acknowledgment">1 (acknowledged emotion)</component>
      <component name="validation">0 ("but" negates validation)</component>
      <component name="ownership">0 (deflected blame to volume)</component>
      <component name="tone">0 (defensive)</component>
      <reasoning>The emotion is acknowledged, but the "but" cancels the validation and the agent deflects blame to volume: needs improvement, 45.</reasoning>
    </analysis>
    <improvement>
      Never use "but" after validation - it negates empathy.
      Better: "I completely understand your frustration. 3 weeks is way too long. Let me fix this for you right now."
    </improvement>
    <risk_flags>
      <flag severity="medium">Defensive tone may escalate customer</flag>
    </risk_flags>
  </example>

  <example category="de_escalation" score="80" tier="proficient">
    <dialogue>
      <customer speaker="customer" timestamp="01:10">This is unacceptable! I've been waiting 3 weeks!</customer>
      <agent speaker="agent" timestamp="01:15">
        I completely understand your frustration, and I sincerely apologize. 3 weeks is absolutely too long. You shouldn't have had to wait this long. Let me look into this right now and get you a resolution today.
      </agent>
    </dialogue>
    <analysis>
      <component name="acknowledgment">1 (acknowledged emotion)</component>
      <component name="validation">1 (agreed it's too long)</component>
      <component name="ownership">1 (apologized, no excuses)</component>
      <component name="tone">1 (empathetic, action-oriented)</component>
      <reasoning>All four components are present and delivered naturally: proficient, 80.</reasoning>
    </analysis>
    <threshold_note>
      Score 70+ requires all 4 components:
      - Acknowledge emotion without "but"
      - Validate customer's concern
      - Own the problem (apologize)
      - Commit to specific action
    </threshold_note>
  </example>

  <borderline_calibration score_A="45" score_B="80">
    <question>What separates Poor de-escalation (45) from Proficient (80)?</question>
    <answer>
      - 80+: Acknowledges + validates + owns + commits (no "but")
      - 45: Acknowledges but deflects blame or makes excuses
      - 80+: "I apologize" (ownership), 45: "We're busy" (excuse)
      - 80+: Specific action commitment, 45: vague or no action
      - 80+: Empathetic tone, 45: defensive or mechanical
    </answer>
  </borderline_calibration>

  <!-- Risk Assessment: Missed (20) vs Identified (90) -->

  <example category="risk_assessment" score="20" tier="critical">
    <dialogue>
      <customer speaker="customer" timestamp="02:20">
        I'm calling my lawyer about this data breach. You leaked my personal information!
      </customer>
      <agent speaker="agent" timestamp="02:25">
        I'm sorry to hear that. Let me reset your password for you.
      </agent>
    </dialogue>
    <analysis>
      <legal_threat_detected>Yes ("calling my lawyer")</legal_threat_detected>
      <data_security_issue>Yes (alleged data breach)</data_security_issue>
      <escalation_performed>No (agent tried to resolve alone)</escalation_performed>
      <severity>CRITICAL</severity>
      <calculation>Explicit legal threat and data breach claim ignored, no escalation = 20/100 (Critical band: fundamental failure)</calculation>
    </analysis>
    <critical_failure>
      Agent ignored an explicit legal threat and failed to escalate the data breach claim.
      MUST escalate immediately to legal/security team.
      Password reset does not address breach allegation.
    </critical_failure>
    <correct_response>
      "I take this very seriously. Let me escalate this to our security team immediately. They will contact you within 1 hour to investigate."
    </correct_response>
  </example>

  <example category="risk_assessment" score="90" tier="exceptional">
    <dialogue>
      <customer speaker="customer" timestamp="02:20">
        I'm calling my lawyer about this data breach. You leaked my personal information!
      </customer>
      <agent speaker="agent" timestamp="02:25">
        I take this extremely seriously. Data security is our top priority. I'm escalating this to our security team right now, and they will contact you within the hour to investigate. May I have your contact information to ensure they reach you immediately?
      </agent>
    </dialogue>
    <analysis>
      <legal_threat_detected>Yes</legal_threat_detected>
      <data_security_issue>Yes</data_security_issue>
      <escalation_performed>Yes (immediate escalation)</escalation_performed>
      <follow_up_committed>Yes (within 1 hour)</follow_up_committed>
      <severity>CRITICAL</severity>
      <calculation>Critical risk escalated immediately = 90/100</calculation>
    </analysis>
    <threshold_note>
      Score 70+ requires:
      - Recognize legal/security keywords ("lawyer", "breach", "sue")
      - Immediate escalation (not attempt to resolve)
      - Specific follow-up timeline
      - Document incident for legal team
    </threshold_note>
  </example>

  <borderline_calibration score_A="20" score_B="90">
    <question>What separates Missed risk (20) from Proper escalation (90)?</question>
    <answer>
      - 90+: Recognizes legal/security keywords, escalates immediately
      - 20: Misses severity, ignores the legal threat, tries to resolve at agent level
      - 90+: Commits to specific follow-up timeline
      - 20: Generic resolution attempt (password reset, refund)
      - Critical keywords: "lawyer", "sue", "breach", "discrimination", "media"
    </answer>
  </borderline_calibration>

  <!-- Solution Offering: Weak (50) vs Strong (85) -->

  <example category="solution_offering" score="50" tier="developing">
    <dialogue>
      <customer speaker="customer" timestamp="03:30">
        My order was supposed to arrive last week for my daughter's birthday. Now it's ruined.
      </customer>
      <agent speaker="agent" timestamp="03:35">
        I'm sorry. I can refund you.
      </agent>
    </dialogue>
    <analysis>
      <solution_offered>Yes (refund)</solution_offered>
      <solution_quality>Generic</solution_quality>
      <empathy_shown>Minimal ("I'm sorry")</empathy_shown>
      <additional_recovery>No</additional_recovery>
      <calculation>Basic solution without recovery = 50/100</calculation>
    </analysis>
    <improvement>
      Acknowledge emotional impact + offer solution + recovery gesture:
      "I'm so sorry we ruined your daughter's birthday. That's unacceptable. Let me refund you fully and send a replacement with overnight shipping at no cost. Plus I'd like to add a $25 credit for the inconvenience."
    </improvement>
  </example>

  <example category="solution_offering" score="85" tier="proficient">
    <dialogue>
      <customer speaker="customer" timestamp="03:30">
        My order was supposed to arrive last week for my daughter's birthday. Now it's ruined.
      </customer>
      <agent speaker="agent" timestamp="03:35">
        I'm so sorry we missed your daughter's birthday. That's completely unacceptable and I understand how disappointing this is. Here's what I'm doing right now: full refund processed today, and I'm sending a replacement with overnight shipping at no cost to you. I'm also adding a $25 credit to your account as an apology. You'll receive tracking within 30 minutes.
      </agent>
    </dialogue>
    <analysis>
      <solution_offered>Yes (refund + replacement + credit)</solution_offered>
      <solution_quality>Comprehensive</solution_quality>
      <empathy_shown>High (acknowledged emotional impact)</empathy_shown>
      <additional_recovery>Yes ($25 credit)</additional_recovery>
      <timeline_committed>Yes (tracking in 30 min)</timeline_committed>
      <calculation>Solution + recovery + timeline = 85/100</calculation>
    </analysis>
    <threshold_note>
      Score 70+ requires:
      - Acknowledge emotional impact (not just "sorry")
      - Primary solution (refund/replacement)
      - Recovery gesture (credit/discount)
      - Specific timeline commitment
    </threshold_note>
  </example>

  <borderline_calibration score_A="50" score_B="85">
    <question>What separates Weak solution (50) from Strong recovery (85)?</question>
    <answer>
      - 85+: Solution + recovery gesture + timeline
      - 50: Basic solution only (refund or replacement)
      - 85+: Acknowledges emotional impact specifically
      - 50: Generic "I'm sorry"
      - 85+: Over-delivers to recover relationship
      - 50: Minimum required action
    </answer>
  </borderline_calibration>

  <!-- Compensation Handling: Inappropriate (40) vs Appropriate (80) -->

  <example category="compensation_handling" score="40" tier="needs_improvement">
    <dialogue>
      <customer speaker="customer" timestamp="04:15">Your service has been down for 2 hours. I'm losing money!</customer>
      <agent speaker="agent" timestamp="04:20">
        I'll give you 3 months free as compensation.
      </agent>
    </dialogue>
    <analysis>
      <compensation_offered>Yes (3 months free)</compensation_offered>
      <authority_level>Likely exceeds agent authority</authority_level>
      <business_impact>High (revenue loss)</business_impact>
      <issues>
        - Over-compensation without approval
        - No investigation of actual impact
        - May set precedent for future claims
      </issues>
      <calculation>Excessive compensation without authority = 40/100</calculation>
    </analysis>
    <improvement>
      "I understand the urgency. Our engineers are working on it right now. Once resolved, I'll work with my manager on appropriate compensation based on the actual downtime. We typically provide service credits for outages."
    </improvement>
    <risk_flags>
      <flag severity="high">Agent may have exceeded authority limits</flag>
    </risk_flags>
  </example>

  <example category="compensation_handling" score="80" tier="proficient">
    <dialogue>
      <customer speaker="customer" timestamp="04:15">Your service has been down for 2 hours. I'm losing money!</customer>
      <agent speaker="agent" timestamp="04:20">
        I completely understand the business impact. Our engineering team is working on this urgently - ETA 30 minutes. Once service is restored, I'll immediately escalate to my manager to discuss appropriate compensation based on your service level agreement. We take downtime very seriously and will make this right.
      </agent>
    </dialogue>
    <analysis>
      <compensation_offered>Conditional (after resolution)</compensation_offered>
      <authority_respected>Yes (escalating to manager)</authority_respected>
      <business_impact_acknowledged>Yes</business_impact_acknowledged>
      <timeline_provided>Yes (30 min ETA)</timeline_provided>
      <sla_referenced>Yes</sla_referenced>
      <calculation>Appropriate compensation process = 80/100</calculation>
    </analysis>
    <threshold_note>
      Score 70+ requires:
      - Acknowledge impact without over-committing
      - Reference policy/SLA
      - Escalate if beyond authority
      - Commit to follow-up after resolution
    </threshold_note>
  </example>

  <borderline_calibration score_A="40" score_B="80">
    <question>What separates Over-compensation (40) from Appropriate (80)?</question>
    <answer>
      - 80+: References policy/SLA, stays within authority
      - 40: Offers excessive compensation without approval
      - 80+: "I'll escalate to manager" (respects limits)
      - 40: Makes promises agent can't keep
      - 80+: Conditional on investigation/resolution
      - 40: Immediate offer without assessing actual impact
    </answer>
  </borderline_calibration>

  <!-- Follow-up Commitment: Vague (35) vs Specific (90) -->

  <example category="follow_up_commitment" score="35" tier="needs_improvement">
    <dialogue>
      <agent speaker="agent" timestamp="05:45">
        We'll look into this and get back to you soon.
      </agent>
    </dialogue>
    <analysis>
      <timeline_specific>No ("soon" is vague)</timeline_specific>
      <owner_identified>No ("we" is generic)</owner_identified>
      <contact_method>Not specified</contact_method>
      <calculation>Vague commitment = 35/100</calculation>
    </analysis>
    <improvement>
      "I'm personally owning this issue. I will investigate with our technical team today and call you back by 5pm ET at the number you provided. If I don't reach you, I'll email you at john@email.com with an update. You also have my direct extension: 555-1234."
    </improvement>
  </example>

  <example category="follow_up_commitment" score="90" tier="exceptional">
    <dialogue>
      <agent speaker="agent" timestamp="05:45">
        I'm personally owning this issue, John. I will investigate with our technical team and call you back by 5pm today at 555-9876. If I can't reach you, I'll email you at john@company.com with a detailed update. I'll also send you a summary email within the hour confirming everything we discussed. My direct extension is 555-1234 if you need to reach me.
      </agent>
    </dialogue>
    <analysis>
      <timeline_specific>Yes (5pm today)</timeline_specific>
      <owner_identified>Yes (agent named, direct extension)</owner_identified>
      <contact_method>Multiple (call + email + extension)</contact_method>
      <backup_plan>Yes (email if no answer)</backup_plan>
      <confirmation>Yes (summary email within hour)</confirmation>
      <calculation>Specific commitment with backup = 90/100</calculation>
    </analysis>
    <threshold_note>
      Score 70+ requires:
      - Specific date/time (not "soon")
      - Named owner (not "we")
      - Contact method specified
      - Backup plan if primary fails
    </threshold_note>
  </example>

  <borderline_calibration score_A="35" score_B="90">
    <question>What separates Vague follow-up (35) from Specific commitment (90)?</question>
    <answer>
      - 90+: Specific date/time + named owner + contact method + backup
      - 35: Vague ("soon", "we'll get back to you")
      - 90+: Personal ownership ("I'm owning this")
      - 35: Generic team reference ("we", "our team")
      - 90+: Multiple contact methods, 35: no method specified
    </answer>
  </borderline_calibration>

  <!-- Comprehensive Complaint Example -->

  <comprehensive_example>
    <complaint_summary>
      High-severity complaint with legal threat, requiring de-escalation, risk assessment, and recovery
    </complaint_summary>
    <dialogue>
      <customer timestamp="00:05">I'm furious! You charged me twice and now my card is overdrawn. I'm contacting my bank to dispute this fraud!</customer>
      <agent timestamp="00:10">I'm so sorry this happened. Double-charging is completely unacceptable and I understand your frustration. Let me investigate this immediately.</agent>
      <agent timestamp="00:25">[investigates] I can see the duplicate charge. This was a processing error on our end. I'm refunding the duplicate charge right now - you'll see it within 24 hours.</agent>
      <agent timestamp="00:35">I'm also adding a $50 credit to your account as an apology for the overdraft fees. Please send me any bank fees you incurred and I'll work with my manager to reimburse those as well.</agent>
      <agent timestamp="00:45">I'm documenting this entire interaction and will personally follow up with you tomorrow at 2pm to confirm the refund posted. My direct line is 555-1234 if you need anything before then.</agent>
      <customer timestamp="00:50">OK, thank you. I appreciate you taking ownership.</customer>
    </dialogue>
    <comprehensive_analysis>
      <de_escalation>
        <score>85</score>
        <reasoning>Acknowledged frustration, validated concern, owned problem, no excuses</reasoning>
      </de_escalation>
      <risk_assessment>
        <score>75</score>
        <reasoning>Recognized "fraud" and "dispute" keywords, took immediate action. Should have flagged for fraud team review.</reasoning>
      </risk_assessment>
      <solution_offering>
        <score>90</score>
        <reasoning>Refund + $50 credit + offer to reimburse bank fees. Comprehensive recovery.</reasoning>
      </solution_offering>
      <compensation_handling>
        <score>85</score>
        <reasoning>Appropriate compensation ($50 credit), escalated bank fee reimbursement to manager</reasoning>
      </compensation_handling>
      <follow_up_commitment>
        <score>90</score>
        <reasoning>Specific time (2pm tomorrow), direct line provided, documented interaction</reasoning>
      </follow_up_commitment>
      <overall_quality>85</overall_quality>
      <customer_satisfaction>High (customer expressed appreciation)</customer_satisfaction>
    </comprehensive_analysis>
  </comprehensive_example>

  <!-- Critical Risk Scenarios -->

  <critical_risk_scenarios>
    <scenario type="legal_threat">
      <keywords>lawyer, attorney, sue, lawsuit, legal action, court</keywords>
      <required_action>Immediate escalation to legal team, do not admit fault</required_action>
      <response_template>
        "I take this very seriously. I'm escalating this to our legal department immediately. They will contact you within [timeframe]. May I have your contact information?"
      </response_template>
    </scenario>

    <scenario type="data_breach">
      <keywords>breach, leaked, hacked, stolen data, personal information exposed</keywords>
      <required_action>Immediate escalation to security team, document everything</required_action>
      <response_template>
        "I take data security extremely seriously. I'm escalating this to our security team right now for immediate investigation. They will contact you within 1 hour."
      </response_template>
    </scenario>

    <scenario type="discrimination">
      <keywords>discrimination, racist, sexist, harassment, bias</keywords>
      <required_action>Immediate escalation to HR/legal, apologize without admitting company-wide issue</required_action>
      <response_template>
        "I'm deeply sorry you experienced this. This is not acceptable. I'm escalating this to our leadership team immediately to investigate."
      </response_template>
    </scenario>

    <scenario type="media_threat">
      <keywords>social media, Twitter, Facebook, news, reporter, viral</keywords>
      <required_action>Escalate to PR/communications team, do not argue or threaten</required_action>
      <response_template>
        "I understand your frustration. Let me work to resolve this issue for you right now. May I have your contact information so our team can follow up?"
      </response_template>
    </scenario>

    <scenario type="physical_harm">
      <keywords>injury, hurt, hospital, medical, pain, damage</keywords>
      <required_action>Immediate escalation to legal/insurance, document meticulously</required_action>
      <response_template>
        "I'm very sorry to hear this. Your safety is our top priority. I'm escalating this to our safety team immediately. They will contact you within 30 minutes."
      </response_template>
    </scenario>
  </critical_risk_scenarios>

</examples>
`;
